import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, Input, LOCALE_ID, OnDestroy, signal, ViewChild } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { AccountDetailDrilldownViewModel, IChartCriteria } from "@tylertech/tyler-api-types";
import { Chart, ChartConfiguration, ChartEvent, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccountDetailDialogComponent, AccountDetailDialogData } from "../accountCard/accountDetailDialog.component";
import { FiscalActivityModel } from "./fiscalActivityModel";

@Component({
    selector: "general-ledger-fiscal-activity-recap",
    templateUrl: "./fiscalActivityChart.tmpl.html",
    standalone: false
})
export class FiscalActivityChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    chartConfig: ChartConfiguration<"bar">;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    isLoaded: boolean;
    ChartSubtitle: string;
    constructor(
        @Inject(LOCALE_ID)
        private readonly locale: string,
        private readonly matDialog: MatDialog,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService,
        private readonly $chartRegistryService: ChartRegistryService,
        $chartManagerService: ChartManagerService,
        private readonly $chartDialogService: ChartDialogService
    )
    {
        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        return "Fiscal Activity";
    }

    get hasChartCriteria(): boolean
    {
        return !!this.chartCriteria?.Parameters;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            } catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private canFetch(): boolean
    {
        return !!this.getParameter(this.chartCriteria, "AccountId");
    }

    private getActivities(): [number, number]
    {
        const data: Array<FiscalActivityModel> = this.chartCriteria.ChartDatas[0].Data;
        const [priorActivity, currentActivity] = data.reduce(
            ([p, c]: [number, number], x: FiscalActivityModel): [number, number] =>
            {
                return [p + x.ActivityPriorFiscal, c + x.ActivityCurrentFiscal];
            },
            [0, 0]
        );
        return [priorActivity, currentActivity];
    }

    private getParameter<T = unknown>(crit: IChartCriteria, name: string): T
    {
        const parameter = crit?.Parameters?.find(x => x.Id === name);
        if (!parameter)
        {
            return null;
        }
        return parameter.Value as T;
    }

    private hasData(): boolean
    {
        if (!this.chartCriteria?.ChartDatas || !this.chartCriteria.ChartDatas[0].Data)
        {
            return false;
        }
        const [priorActivity, currentActivity] = this.getActivities();
        return priorActivity !== 0 && currentActivity !== 0;
    }

    private loadChartData(): Promise<void>
    {
        if (!this.canFetch())
        {
            return Promise.resolve();
        }
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    } catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private onClick(event: ChartEvent & Event, _activeElements: Array<unknown>, chart: Chart): void
    {
        const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
        const element = elements[0];
        if (!element)
        {
            return;
        }
        const current = element.datasetIndex === 0;
        const fiscal = current ? "FiscalId" : "PriorFiscalId";
        const fiscalId = this.getParameter<string>(this.chartCriteria, fiscal);
        if (!fiscalId)
        {
            return;
        }
        const info: AccountDetailDrilldownViewModel = {
            Id: this.getParameter<string>(this.chartCriteria, "AccountId"),
            AccountType: "G",
            IdType: "Account",
            FiscalId: fiscalId,
            PeriodId: null,
        };
        const dialogConfig: MatDialogConfig<AccountDetailDialogData> = {
            data: {
                fetch: info,
                type: "Activity",
            },
        };
        this.matDialog.open(AccountDetailDialogComponent, dialogConfig);
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    updateChartConfiguration(): void
    {
        if (
            !this.chartCriteria.ChartDatas[0].Labels ||
            this.chartCriteria.ChartDatas[0].Labels.length === 0 ||
            this.chartCriteria.ChartDatas[0].Labels.length === 12
        )
        {
            this.chartCriteria.ChartDatas[0].Labels = ["Current", "Prior"];
        }
        const [priorActivity, currentActivity] = this.getActivities();

        const activityColor = rgb(0, 143, 140, 1);
        const priorActivityColor = rgb(13, 38, 68, 1);

        this.chartConfig = {
            type: "bar",
            options: {
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, color: "#333333" },
                        onHover: (e: ChartEvent, _legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            legend.chart.canvas.style.cursor = e ? "pointer" : "default";
                        },
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | Array<string> =>
                            {
                                const label = tooltipItem.label;
                                const amount = formatCurrency(tooltipItem.raw as number, this.locale, "$");
                                return `${label}: ${amount}`;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                        labels: [""],
                    },
                    y: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
                onClick: this.onClick.bind(this),
            },
            data: {
                datasets: [
                    {
                        label: this.chartCriteria.ChartDatas[0].Labels[0],
                        data: [currentActivity],
                        backgroundColor: activityColor,
                        borderColor: activityColor,
                        hoverBackgroundColor: activityColor,
                        hoverBorderColor: activityColor,
                        yAxisID: "y",
                        barPercentage: 0.8,
                    },
                    {
                        label: this.chartCriteria.ChartDatas[0].Labels[1],
                        data: [priorActivity],
                        backgroundColor: priorActivityColor,
                        borderColor: priorActivityColor,
                        hoverBackgroundColor: priorActivityColor,
                        hoverBorderColor: priorActivityColor,
                        yAxisID: "y",
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
